import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { creditAccountService } from "../services/creditAccountService";
import { creditAccountDisplayId } from "./CreditAccountCommonStore";
import { CreditAccount } from "../../../api/graphql";
import { creditTierDisplayId } from "../../creditTier/store/CreditTierCommonStore";
import { getCreditMovementValues } from "../../creditMovement/store/CreditMovementCommonStore";
import { ColumnWidthInfo } from "../../common/services/crudServices";
import { CrudRows } from "../../common/types";
import { creditTierService } from "../../creditTier/services/creditTierService";
import { InitializeParams } from "../../../store/types";

export class CreditAccountViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = creditAccountService;
  creditAccountInstance: CreditAccount | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  balanceValue: number = 0;
  tierService = creditTierService;
  tierSearchValue: string = "";
  tierIdValue: string = "";
  tierSelectedValue: { displayId: string; id: string } | null = null;
  movementsColumnWidths: Record<string, number | string> = {};
  movementsRows: CrudRows = {};

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;
    this.movementsColumnWidths = {
      movementAt: 200,
      type: 200,
      delta: 200,
      balanceAfter: 200,
      detail: 200,
    };

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadCreditAccountById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.creditAccountOne({ id });
    this.creditAccountInstance = result.creditAccountOne;
    if (this.creditAccountInstance) {
      this.loadValues(this.creditAccountInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.creditAccountInstance) {
      return creditAccountDisplayId(this.creditAccountInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.CreditAccountSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "tier") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.creditTierOne({
          id: params.returnData.resultId,
        });
        const creditTier = result.creditTierOne;
        this.tierSelectedValue = creditTier;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get tierValue() {
    if (this.tierSelectedValue) {
      return {
        id: this.tierSelectedValue.id,
        displayId: creditTierDisplayId(this.tierSelectedValue, this.langStore),
      };
    }
    return null;
  }

  get movementsVisibleColumns() {
    return [
      {
        name: "movementAt",
        title: this.langStore.CreditMovementMovementAtLabel,
        width: this.movementsColumnWidths["movementAt"],
      },
      {
        name: "type",
        title: this.langStore.CreditMovementTypeLabel,
        width: this.movementsColumnWidths["type"],
      },
      {
        name: "delta",
        title: this.langStore.CreditMovementDeltaLabel,
        width: this.movementsColumnWidths["delta"],
      },
      {
        name: "balanceAfter",
        title: this.langStore.CreditMovementBalanceAfterLabel,
        width: this.movementsColumnWidths["balanceAfter"],
      },
      {
        name: "detail",
        title: this.langStore.CreditMovementDetailLabel,
        width: this.movementsColumnWidths["detail"],
      },
    ];
  }

  movementsSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.movementsColumnWidths = values;
  }

  get movementsRowsDyn() {
    return this.movementsRows;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.creditAccountInstance = null;
    this.balanceValue = 0;
    this.tierSearchValue = "";
    this.tierIdValue = "";
    this.movementsRows = {};
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelCreditAccount() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("creditAccount_update");
  }

  editCreditAccount() {
    const id = this.creditAccountInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/creditAccount/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printCreditAccount(handlePrint: () => void): void {
    handlePrint();
  }

  *closeCreditAccount(): any {
    yield this.close();
  }

  loadValues(instance: CreditAccount) {
    this.idValue = instance.id;
    this.balanceValue = instance.balance;
    this.tierSelectedValue = instance.tier ? instance.tier : null;
    this.movementsRows = instance.movements
      ? Object.assign(
          {},
          ...instance.movements.map((x) => {
            const processedRow = getCreditMovementValues(
              x,
              this.langStore,
              false
            );
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadCreditAccountById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
